import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { AlertController, IonicModule, LoadingController, ToastController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { GratificationService, GratificationData, GratificationStatusResponse } from 'src/app/services/api/gratification.service';
import { StripeService } from 'src/app/services/stripe.service';
import { StripeConnectStateService } from 'src/app/services/stripe-connect-state.service';
import { StripeConnectAccountState } from 'src/app/types/stripe-connect';
import { TranslateConfigService } from 'src/app/services/translate-config.service';
import { BrowserService } from 'src/app/services/browser.service';
import { ChatPusherService } from 'src/app/services/chat-pusher.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DialogComponent } from '../../dialog/dialog.component';
import { Subscription } from 'rxjs';

/**
 * Componente de gratificación voluntaria para el chat.
 *
 * Muestra diferentes estados según el rol del usuario (propietario/finder)
 * y el estado actual de la gratificación.
 *
 * Se integra después de la validación del intercambio en la página de mensajes.
 */
@Component({
  selector: 'app-gratification-banner',
  templateUrl: './gratification-banner.component.html',
  styleUrls: ['./gratification-banner.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, FormsModule, TranslateModule, DialogComponent]
})
export class GratificationBannerComponent implements OnInit, OnChanges, OnDestroy {
  /** ID de la conversación actual */
  @Input() conversationId!: number;
  /** ID del usuario autenticado */
  @Input() currentUserId!: number;
  /** Si el intercambio ya fue validado */
  @Input() isExchangeValidated: boolean = false;

  /** Emitido cuando el estado de la gratificación cambia (para refrescar contexto en el padre) */
  @Output() gratificationChanged = new EventEmitter<GratificationData | null>();

  gratification: GratificationData | null = null;
  canSend: boolean = false;
  loading: boolean = false;
  amountInput: number | null = null;
  showSendForm: boolean = false;
  showSendConfirmDialog: boolean = false;
  sendTermsAccepted: boolean = false;

  /** Estado de Stripe Connect del receptor (solo se carga cuando es receiver + pending_acceptance) */
  connectState: StripeConnectAccountState | null = null;
  connectLoading: boolean = false;
  private chatEventsSubscription: Subscription | null = null;

  readonly MAX_AMOUNT = 200;
  readonly MIN_AMOUNT = 0.50;

  constructor(
    private gratificationService: GratificationService,
    private stripeService: StripeService,
    private stripeConnectState: StripeConnectStateService,
    private alertController: AlertController,
    private loadingCtrl: LoadingController,
    private toastCtrl: ToastController,
    private browserService: BrowserService,
    private chatPusherService: ChatPusherService,
    public t: TranslateConfigService,
  ) {}

  ngOnInit(): void {
    void this.initializeChatEventSubscription();

    if (this.isExchangeValidated && this.conversationId) {
      this.fetchStatus();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const conversationChanged: boolean = !!changes['conversationId'];
    const currentUserChanged: boolean = !!changes['currentUserId'];
    const shouldResetState: boolean = conversationChanged || currentUserChanged;

    if (shouldResetState) {
      this.resetBannerState();
    }

    if ((changes['isExchangeValidated'] || currentUserChanged || conversationChanged) && this.isExchangeValidated && this.conversationId) {
      if ((currentUserChanged || conversationChanged) && this.chatEventsSubscription) {
        this.chatEventsSubscription.unsubscribe();
        this.chatEventsSubscription = null;
      }

      void this.initializeChatEventSubscription();
      this.fetchStatus();
    }
  }

  ngOnDestroy(): void {
    this.chatEventsSubscription?.unsubscribe();
    this.chatEventsSubscription = null;
  }

  // ─── Estado ────────────────────────────────────────────────

  get isSender(): boolean {
    return this.matchesCurrentUser(this.gratification?.sender_id);
  }

  get isReceiver(): boolean {
    return this.matchesCurrentUser(this.gratification?.receiver_id);
  }

  /**
   * Compara un id (sender/receiver) con el usuario actual de forma tolerante al
   * tipo. El backend puede devolver los ids como string (las claves foráneas no
   * se castean) mientras que currentUserId es number; con `===` estricto el rol
   * no se detectaba y en web no aparecían el título ni los botones de
   * aceptar/rechazar la recompensa.
   */
  private matchesCurrentUser(id: number | string | null | undefined): boolean {
    const target = Number(this.currentUserId);
    const value = Number(id);
    return !Number.isNaN(target) && !Number.isNaN(value) && value === target;
  }

  get statusKey(): string {
    return this.gratification?.status ?? '';
  }

  get isPendingAcceptance(): boolean {
    return this.statusKey === 'pending_acceptance';
  }

  get isPendingPayment(): boolean {
    return this.statusKey === 'pending_payment';
  }

  get isAccepted(): boolean {
    return this.statusKey === 'accepted';
  }

  get isTransferred(): boolean {
    return this.statusKey === 'transferred';
  }

  get isRefunded(): boolean {
    return this.statusKey === 'refunded';
  }

  get isRejected(): boolean {
    return this.statusKey === 'rejected';
  }

  get isExpired(): boolean {
    return this.statusKey === 'expired';
  }

  get isTerminal(): boolean {
    return this.isTransferred || this.isRefunded;
  }

  /** True si el Finder ya tiene Stripe Connect listo para recibir pagos */
  get isConnectReady(): boolean {
    return this.stripeConnectState.isOnboarded(this.connectState ?? undefined);
  }

  get showBanner(): boolean {
    return this.isExchangeValidated && (this.canSend || !!this.gratification);
  }

  get canResumePendingPayment(): boolean {
    return this.canSend && this.isPendingPayment && this.isSender;
  }

  get showSendCard(): boolean {
    return this.canSend && (!this.gratification || this.canResumePendingPayment);
  }

  private applyStatusResponse(response: GratificationStatusResponse): void {
    this.gratification = response.gratification;
    this.canSend = response.can_send;

    if (this.canResumePendingPayment) {
      this.amountInput = this.gratification?.amount ?? this.amountInput;
      this.showSendForm = true;
    } else if (!this.gratification) {
      this.showSendForm = false;
    }

    this.gratificationChanged.emit(this.gratification);
  }

  private resetBannerState(): void {
    this.gratification = null;
    this.canSend = false;
    this.amountInput = null;
    this.showSendForm = false;
    this.showSendConfirmDialog = false;
    this.sendTermsAccepted = false;
    this.connectState = null;
    this.connectLoading = false;
  }

  // ─── API calls ─────────────────────────────────────────────

  async fetchStatus(): Promise<void> {
    if (!this.conversationId) return;
    try {
      const res = await firstValueFrom(this.gratificationService.getStatus(this.conversationId));
      this.applyStatusResponse(res);

      // Si el receptor ve la gratificación pendiente de aceptación, comprobar su cuenta Stripe
      if (this.isPendingAcceptance && this.isReceiver) {
        await this.fetchConnectStatus();
      }
    } catch (err) {
    }
  }

  private async initializeChatEventSubscription(): Promise<void> {
    if (!this.currentUserId || this.chatEventsSubscription) {
      return;
    }

    if (!this.chatPusherService.subscribed) {
      await this.chatPusherService.initPusherSubscription(this.currentUserId);
    }

    this.chatEventsSubscription = this.chatPusherService.onConversationUpdated$.subscribe(({ data }) => {
      if (Number(data?.user_id) !== Number(this.currentUserId)) {
        return;
      }

      void this.fetchStatus();
    });
  }

  /** Comprueba el estado de Stripe Connect del receptor */
  async fetchConnectStatus(): Promise<void> {
    if (this.connectLoading) return;
    this.connectLoading = true;
    try {
      this.connectState = await this.stripeConnectState.refreshStatus();
    } catch (err) {
      this.connectState = null;
    } finally {
      this.connectLoading = false;
    }
  }

  /** Abre el flujo de alta en Stripe Connect */
  async openConnectOnboarding(): Promise<void> {
    await this.stripeConnectState.startOnboardingFlow();
    // Refrescar estado tras volver del onboarding
    await this.fetchConnectStatus();
  }

  toggleSendForm(): void {
    this.showSendForm = !this.showSendForm;
    if (this.showSendForm) {
      this.amountInput = this.canResumePendingPayment
        ? (this.gratification?.amount ?? this.amountInput)
        : null;
    }
  }

  requestSendConfirmation(): void {
    if (!this.amountInput || this.amountInput < this.MIN_AMOUNT || this.amountInput > this.MAX_AMOUNT) {
      void this.showToast(
        this.t.getItemTranslated('GRATIFICATION.INVALID_AMOUNT') || 'Invalid amount',
        'warning',
      );
      return;
    }

    this.showSendConfirmDialog = true;
  }

  cancelSendConfirmation(): void {
    this.showSendConfirmDialog = false;
    this.sendTermsAccepted = false;
  }

  async confirmSendGratification(): Promise<void> {
    this.showSendConfirmDialog = false;
    this.sendTermsAccepted = false;
    await this.sendGratification();
  }

  async openTerms(event: MouseEvent): Promise<void> {
    const target = event.target as HTMLElement;
    if (target.classList.contains('terms-link')) {
      event.preventDefault();
      event.stopPropagation();
      await this.browserService.openTermsAndConditions();
    }
  }

  async sendGratification(): Promise<void> {
    if (!this.amountInput || this.amountInput < this.MIN_AMOUNT || this.amountInput > this.MAX_AMOUNT) {
      await this.showToast(
        this.t.getItemTranslated('GRATIFICATION.INVALID_AMOUNT') || 'Invalid amount',
        'warning',
      );
      return;
    }

    this.loading = true;

    try {
      // Ejecutar el flujo de pago de Stripe.
      // Cada prepare* crea la gratificación con el endpoint adecuado:
      //   - Native → PaymentIntent (para PaymentSheet)
      //   - Web    → Checkout Session (para Embedded Checkout)
      const paymentResult = await this.stripeService.executeCustomFlow({
        prepareNative: async () => {
          const sendRes = await firstValueFrom(
            this.gratificationService.send(this.conversationId, this.amountInput!),
          );
          if (!sendRes.success) {
            throw new Error('Failed to create gratification');
          }
          return {
            reference: String(sendRes.gratification_id),
            options: {
              paymentIntentClientSecret: sendRes.clientSecret,
              customerId: sendRes.customerId,
              customerEphemeralKeySecret: sendRes.ephemeralKey,
              merchantDisplayName: 'Foundek',
            },
          };
        },
        prepareWeb: async () => {
          const webRes = await firstValueFrom(
            this.gratificationService.sendWeb(this.conversationId, this.amountInput!),
          );
          if (!webRes.success) {
            throw new Error('Failed to create gratification');
          }
          return {
            reference: String(webRes.gratification_id),
            clientSecret: webRes.session.client_secret,
          };
        },
        loadingKey: 'COMMON.LOADING',
        processingKey: 'GRATIFICATION.PROCESSING',
        errorKey: 'GRATIFICATION.ERROR',
      });

      const verifiedResult = await this.stripeService.verifyPaymentOutcome({
        initialResult: paymentResult,
        verify: async () => firstValueFrom(this.gratificationService.getStatus(this.conversationId)),
        isVerified: (statusResponse) => {
          return (
            statusResponse.success &&
            !!statusResponse.gratification &&
            statusResponse.gratification.status !== 'pending_payment'
          );
        },
      });

      if (verifiedResult.success && verifiedResult.verification) {
        this.applyStatusResponse(verifiedResult.verification);
        await this.showToast(
          this.t.getItemTranslated('GRATIFICATION.SENT_SUCCESS') || 'Gratification sent!',
          'success',
        );
        this.showSendForm = false;

        if (this.isPendingAcceptance && this.isReceiver) {
          await this.fetchConnectStatus();
        }

        return;
      }

      if (paymentResult.success) {
        await this.showToast(
          this.t.getItemTranslated('GRATIFICATION.PROCESSING') || 'Procesando pago...',
          'medium',
        );
        this.showSendForm = false;
        return;
      }

      if (verifiedResult.canceled) {
        await this.fetchStatus();
        if (this.canResumePendingPayment) {
          this.showSendForm = true;
        }
        return;
      }

      if (!verifiedResult.canceled) {
        throw verifiedResult.error ?? new Error('GRATIFICATION.ERROR');
      }
    } catch (err: any) {
      if (err?.message !== 'Payment canceled' && err?.message !== 'WEB_CHECKOUT_CLOSED') {
        await this.showToast(
          this.t.getItemTranslated('GRATIFICATION.ERROR') || 'An error occurred',
          'danger',
        );
      }
    } finally {
      this.loading = false;
    }
  }

  async acceptGratification(): Promise<void> {
    if (!this.gratification) return;

    const alert = await this.alertController.create({
      header: this.t.getItemTranslated('GRATIFICATION.ACCEPT_TITLE') || 'Accept gratification',
      message: this.t.getItemTranslated('GRATIFICATION.ACCEPT_CONFIRM') || 'Do you accept this gratification?',
      buttons: [
        { text: this.t.getItemTranslated('COMMON.CANCEL') || 'Cancel', role: 'cancel' },
        {
          text: this.t.getItemTranslated('COMMON.ACCEPT') || 'Accept',
          handler: () => this.doAccept(),
        },
      ],
    });
    await alert.present();
  }

  private async doAccept(): Promise<void> {
    if (!this.gratification) return;
    this.loading = true;
    try {
      await firstValueFrom(this.gratificationService.accept(this.gratification.id));
      await this.showToast(
        this.t.getItemTranslated('GRATIFICATION.ACCEPTED_SUCCESS') || 'Gratification accepted',
        'success',
      );
      await this.fetchStatus();
    } catch (err) {
      await this.showToast(
        this.t.getItemTranslated('GRATIFICATION.ERROR') || 'Error',
        'danger',
      );
    } finally {
      this.loading = false;
    }
  }

  async rejectGratification(): Promise<void> {
    if (!this.gratification) return;

    const alert = await this.alertController.create({
      header: this.t.getItemTranslated('GRATIFICATION.REJECT_TITLE') || 'Reject gratification',
      message: this.t.getItemTranslated('GRATIFICATION.REJECT_CONFIRM') || 'Are you sure? The amount will be refunded.',
      buttons: [
        { text: this.t.getItemTranslated('COMMON.CANCEL') || 'Cancel', role: 'cancel' },
        {
          text: this.t.getItemTranslated('GRATIFICATION.REJECT_BUTTON') || 'Reject',
          role: 'destructive',
          handler: () => this.doReject(),
        },
      ],
    });
    await alert.present();
  }

  private async doReject(): Promise<void> {
    if (!this.gratification) return;
    this.loading = true;
    try {
      await firstValueFrom(this.gratificationService.reject(this.gratification.id));
      await this.showToast(
        this.t.getItemTranslated('GRATIFICATION.REJECTED_SUCCESS') || 'Gratification rejected',
        'success',
      );
      await this.fetchStatus();
    } catch (err) {
      await this.showToast(
        this.t.getItemTranslated('GRATIFICATION.ERROR') || 'Error',
        'danger',
      );
    } finally {
      this.loading = false;
    }
  }

  async retryTransfer(): Promise<void> {
    if (!this.gratification) return;
    this.loading = true;
    try {
      await firstValueFrom(this.gratificationService.retryTransfer(this.gratification.id));
      await this.showToast(
        this.t.getItemTranslated('GRATIFICATION.TRANSFER_SUCCESS') || 'Transfer completed!',
        'success',
      );
      await this.fetchStatus();
    } catch (err) {
      await this.showToast(
        this.t.getItemTranslated('GRATIFICATION.ERROR') || 'Error',
        'danger',
      );
    } finally {
      this.loading = false;
    }
  }

  // ─── Helpers ───────────────────────────────────────────────

  private async showToast(message: string, color: string): Promise<void> {
    const toast = await this.toastCtrl.create({
      message,
      duration: 3000,
      color,
      position: 'top',
    });
    await toast.present();
  }

  formatAmount(amount: number): string {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount);
  }
}
